import { ApolloClient } from '@apollo/client';
import { LocaleCode } from '../../../../../utils/Locale';
import { ResourceLibraryItem, ResourceLibraryItemType } from '../types/resourceLibraryItems';
import {
    GET_RESOURCE_LIBRARY_ARTICLE_ITEM_QUERY,
    getResourceLibraryArticleItemQueryVariables,
} from '../queries/getResourceLibraryArticleItemQuery';
import {
    GET_RESOURCE_LIBRARY_ASSET_ITEM_QUERY,
    getResourceLibraryAssetItemQueryVariables,
} from '../queries/getResourceLibraryAssetItemQuery';
import { transformResourceLibraryArticleItemResponse } from './transformResourceLibraryArticleItemResponse';
import { transformResourceLibraryAssetItemResponse } from './transformResourceLibraryAssetItemResponse';
import {
    GET_RESOURCE_LIBRARY_SITE_PAGE_ITEM_QUERY,
    getResourceLibrarySitePageItemQueryVariables,
} from '../queries/getResourceLibrarySitePageItemQuery';
import { transformResourceLibrarySitePageItemResponse } from './transformResourceLibrarySitePageItemResponse';

export async function loadResourceLibraryItemById(params: {
    apolloClient: ApolloClient<any>;
    kind: ResourceLibraryItemType;
    id: string;
    locale: LocaleCode;
}): Promise<ResourceLibraryItem | null> {
    const { apolloClient, kind, id, locale } = params;

    try {
        if (kind === 'article') {
            const { data } = await apolloClient.query({
                query: GET_RESOURCE_LIBRARY_ARTICLE_ITEM_QUERY,
                variables: getResourceLibraryArticleItemQueryVariables(locale, Number(id)),
                fetchPolicy: 'network-only',
            });
            return transformResourceLibraryArticleItemResponse(data);
        }

        if (kind === 'sitePage') {
            const { data } = await apolloClient.query({
                query: GET_RESOURCE_LIBRARY_SITE_PAGE_ITEM_QUERY,
                variables: getResourceLibrarySitePageItemQueryVariables(locale, Number(id)),
                fetchPolicy: 'network-only',
            });
            return transformResourceLibrarySitePageItemResponse(data);
        }

        if (kind === 'asset') {
            const { data } = await apolloClient.query({
                query: GET_RESOURCE_LIBRARY_ASSET_ITEM_QUERY,
                variables: getResourceLibraryAssetItemQueryVariables(locale, Number(id)),
                fetchPolicy: 'network-only',
            });
            return transformResourceLibraryAssetItemResponse(data, locale);
        }

        if (kind === 'news') {
            const {
                GET_RESOURCE_LIBRARY_NEWS_ITEM_QUERY,
                getResourceLibraryNewsItemQueryVariables,
            } = await import('../queries/getResourceLibraryNewsItemQuery');
            const { transformResourceLibraryNewsItemResponse } = await import(
                './transformResourceLibraryNewsItemResponse'
            );
            const { data } = await apolloClient.query({
                query: GET_RESOURCE_LIBRARY_NEWS_ITEM_QUERY,
                variables: getResourceLibraryNewsItemQueryVariables(locale, Number(id)),
                fetchPolicy: 'network-only',
            });
            return transformResourceLibraryNewsItemResponse(data);
        }

        if (kind === 'newProductIntroduction') {
            const {
                GET_RESOURCE_LIBRARY_NEW_PRODUCT_INTRODUCTION_ITEM_QUERY,
                getResourceLibraryNewProductIntroductionItemQueryVariables,
            } = await import('../queries/getResourceLibraryNewProductIntroductionItemQuery');
            const { transformResourceLibraryNewProductIntroductionItemResponse } = await import(
                './transformResourceLibraryNewProductIntroductionItemResponse'
            );
            const { data } = await apolloClient.query({
                query: GET_RESOURCE_LIBRARY_NEW_PRODUCT_INTRODUCTION_ITEM_QUERY,
                variables: getResourceLibraryNewProductIntroductionItemQueryVariables(
                    locale,
                    Number(id)
                ),
                fetchPolicy: 'network-only',
            });
            return transformResourceLibraryNewProductIntroductionItemResponse(data);
        }
    } catch (e) {
        console.error('Failed to load ResourceLibrary item:', e);
        return null;
    }

    console.warn('Unsupported ResourceLibrary item kind:', kind);
    return null;
}
